import {Button, Divider, Grid, Image, Modal, Typography} from '@yandex-delivery/blocks'
import {spacing} from '@yandex-delivery/blocks/theme/theme'
import {useI18n} from '@yandex-delivery/core/i18n'
import type {ReactElement} from 'react'
import React, {useCallback, useMemo} from 'react'
import {useController, useFormContext} from 'react-hook-form'

import {PhoneInput} from '../../../../components/Input/PhoneInput'
import {TextInput} from '../../../../components/Input/TextInput'
import {useConfigContext} from '../../../../contexts/ConfigContext'
import {tagToUrl} from '../../../../lib/tagToUrl'
import {SuggestDesktop, SuggestMobile} from '../../../../widgets/Address/components/Suggest'
import type {ActionPropsByType, ActionType} from '../../../types'
import * as classes from './ReturnAddressModal.classes'

export function ReturnAddressModal({
  setActive,
  name,
}: Omit<ActionPropsByType<ActionType.MODAL>, 'modal_id'> & {
  name: string
}): ReactElement {
  const i18n = useI18n()
  const {location, device} = useConfigContext()
  const {watch, resetField, setValue, control, trigger} = useFormContext()
  const addressPin = watch(name)
  const {
    fieldState: {error: phoneError, invalid: phoneInvalid},
  } = useController({name: `${name}.phone`, control})

  const fields = useMemo(() => {
    return [
      {name: `${name}.apartment`, label: i18n('bdui.address.input.apartment', 'Кв./офис')},
      {name: `${name}.entrance`, label: i18n('bdui.address.input.entrance', 'Подъезд')},
      {name: `${name}.floor`, label: i18n('bdui.address.input.floor', 'Этаж')},
      {name: `${name}.code`, label: i18n('bdui.address.input.code', 'Домофон')},
    ]
  }, [i18n, name])

  const handleCancelButtonClick = useCallback(() => {
    resetField(name)
    setValue(name, undefined)
    setActive?.(false)
  }, [name, resetField, setActive, setValue])

  const handleSave = useCallback(
    async (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation() // дефолтный Modal.Header пропускает ивенты, заменила на бойлерплейт
      const isFormValid = await trigger(`${name}.phone`)
      if (isFormValid) {
        setActive?.(false)
      }
      return
    },
    [name, setActive, trigger],
  )

  return (
    <Modal className={classes.root} mx="s" onClose={handleCancelButtonClick} preventScroll={false} opened>
      <Modal.Header
        className={classes.header}
        title={i18n('bdui.address.return-point.modal-title', 'Адрес для возврата посылки')}
      />
      <Modal.Body>
        <div className={classes.suggest}>
          {device === 'mobile' ? (
            <SuggestMobile
              addressPin={addressPin}
              coordinates={location}
              isSource={false}
              label={i18n('bdui.address.input.suggest', 'Улица, дом')}
              name={name}
              floatingItems
              isReturnAddress
            />
          ) : (
            <SuggestDesktop
              addressPin={addressPin}
              coordinates={location}
              isSource={false}
              label={i18n('bdui.address.input.suggest', 'Улица, дом')}
              name={name}
              floatingItems
              isReturnAddress
            />
          )}
        </div>

        <Grid direction="column" size="m" container>
          <Grid item xs></Grid>
          <Grid xs={12} container item>
            <Grid justify="flex-start" xs={12} item>
              <Typography thickness="medium" variant="body2">
                {i18n('bdui.address.input.address', 'Адрес')}
              </Typography>
            </Grid>
            <Grid className={classes.grid} container item>
              {fields.map(field => (
                <TextInput key={`return-${name}`} size="l" variant="line" {...field} />
              ))}
            </Grid>
            <TextInput
              className={classes.multiline}
              label={i18n('bdui.address.input.comment', 'Комментарий для курьера')}
              name={`${name}.comment`}
              size="l"
              variant="line"
              multiline
            />
          </Grid>
          <Grid direction="column" size="s" xs={12} container item>
            <Grid justify="flex-start" xs={12} item>
              <Typography thickness="medium" variant="body2">
                {i18n('bdui.address.input.phone-title', 'Телефон')}
              </Typography>
            </Grid>
            <Grid justify="flex-start" xs={12} item>
              <PhoneInput
                className={classes.suggest}
                label={i18n('bdui.address.input.phone', 'Телефон')}
                lead={<Image height={parseInt(spacing(3))} src={tagToUrl('image_user_round_fill')} />}
                name={`${name}.phone`}
                size="l"
                variant="line"
              />
              <Divider isInvalid={phoneInvalid} />
              {phoneInvalid && (
                <Typography className={classes.errorMessage} color="error" variant="caption2">
                  {phoneError?.message}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Modal.Body>
      <Modal.Footer className={classes.modalFooter}>
        <Modal.Footer.Controls>
          <Button data-e2e="button-cancel" onPress={handleCancelButtonClick} size="l">
            {i18n('bdui.button.cancel', 'Отменить')}
          </Button>
          <Button data-e2e="button-save" disabled={phoneInvalid} onClick={handleSave} size="l" variant="action">
            {i18n('bdui.button.submit', 'Сохранить')}
          </Button>
        </Modal.Footer.Controls>
      </Modal.Footer>
    </Modal>
  )
}
